// Phase 4a — fetch the full Brand doc keyed by the active brand id
// from BrandContext. Re-fetches on brand:change.
//
// brand:change is dispatched in the same turn that localStorage.brand_id
// is updated, before React commits the new active brand. A response is
// applied only when its generation is still current, so a slower read
// for the previous id cannot overwrite the brand you switched to.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { statusErrorText } from './connectionStatus';
import type { Brand } from './types';

type State = {
  brand:    Brand | null;
  loading:  boolean;
  error:    string | null;
  refresh:  () => Promise<void>;
};

export function useBrandDetail(): State {
  const { activeBrand } = useBrand();
  const brandId = activeBrand?.id || null;
  const generation = useRef(0);

  const [brand, setBrand]     = useState<Brand | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async (requestedId: string | null) => {
    const gen = ++generation.current;
    if (!requestedId) {
      setBrand(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setBrand((current) => (current && current._id !== requestedId ? null : current));
    try {
      const res = await apiJson<{ brand: Brand }>(`/api/brand/${requestedId}`);
      if (gen !== generation.current) return;
      const next = res?.brand ?? null;
      if (next?._id && next._id !== requestedId) return;
      setBrand(next);
    } catch (err: unknown) {
      if (gen !== generation.current) return;
      const raw = err instanceof Error ? err.message : '';
      setError(statusErrorText(raw, 'Failed to load brand'));
      setBrand(null);
    } finally {
      if (gen === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(brandId);
    return () => { generation.current += 1; };
  }, [brandId, load]);

  useEffect(() => {
    const onChange = () => {
      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('brand_id') : null;
      void load(stored);
    };
    window.addEventListener('brand:change', onChange);
    return () => window.removeEventListener('brand:change', onChange);
  }, [load]);

  const refresh = useCallback(async () => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('brand_id') : null;
    await load(stored || brandId);
  }, [brandId, load]);

  return { brand, loading, error, refresh };
}
